// schema
// import * as ISchema from '@schema';

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// work-address imports
import { IContainer, IProps, IState, WorkAddressMode } from "./WorkAddress.d";
import { workAddressFragments } from "./WorkAddress.gql";
import { AddressSearch } from "@components/address-search";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setWorkAddressForUser } from "@mutations";

// util
import { snackBarOpen } from "@actions/snack-bar.actions";
// import { getCurrentOrg } from '@util/org.util';

import { track, TrackingEvents } from "@util/analytics.util";
import { H2 } from "@ui";
// material-ui
import { Button } from "@clockwise/design-system";
import { Typography } from "@material-ui/core";
import { Edit, Delete } from "@material-ui/icons";

///////////////
// Component //
///////////////
class WorkAddressCmpt extends React.Component<IProps, IState> {
  static defaultProps = {
    mode: "standard" as WorkAddressMode,
    autosave: true,
    save: false,
  };

  constructor(props: IProps) {
    super(props);

    const address =
      (this.props.org.workAddressForUser.__typename !== "GraphEntityError" &&
        this.props.org.workAddressForUser.rawAddress) ||
      null;

    if (address && this.props.onSelectAddress) {
      this.props.onSelectAddress(address);
    }

    this.state = {
      address,
      showAddressSearch: false,
      query: (props.mode === "onboarding" && address) || "",
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { address } = this.state;
    if (
      nextProps.org.workAddressForUser.__typename !== "GraphEntityError" &&
      nextProps.org.workAddressForUser.rawAddress != address
    ) {
      const address = nextProps.org.workAddressForUser.rawAddress || null;
      const query = (this.props.mode === "onboarding" && address) || this.state.query;

      this.setState({ address, query });
    }
    // see SetWorkAddressForUser.mutation, for the initial set of this object
    // attempts to update the record by changing its type from GraphEntityError
    // to address cause an error on store.create, so instead get the address from
    // the updated GraphEntityError object
    if (
      nextProps.org.workAddressForUser.__typename === "GraphEntityError" &&
      nextProps.org.workAddressForUser.statusCode === 200 &&
      nextProps.org.workAddressForUser.message !== address
    ) {
      const address = nextProps.org.workAddressForUser.message || null;
      const query = (this.props.mode === "onboarding" && address) || this.state.query;

      this.setState({ address, query });
    }

    if (nextProps.save && !this.props.save && this.state.address) {
      this.onSave(this.state.address);
    }
  }

  private onClickSetWorkLocation = () => {
    track(TrackingEvents.SETTINGS.WORK_ADDRESS_BEGIN_SEARCH);
    this.setState({ showAddressSearch: true });
  };

  private onCancel = () => {
    this.setState({ showAddressSearch: false });
  };

  private onSelectAddress = (address: string) => {
    track(TrackingEvents.SETTINGS.WORK_ADDRESS_SELECT_RESULT);
    this.setState({ address, showAddressSearch: false });

    if (address && this.props.onSelectAddress) {
      this.props.onSelectAddress(address);
    }

    if (this.props.autosave) {
      this.onSave(address);
    }
  };

  private onSave = (address: string) => {
    const { org } = this.props;

    setWorkAddressForUser(
      this.props.relay.environment,
      { orgRelayId: org.id, rawAddress: address },
      this.onSetAddressSuccess,
      this.onSetAddressFailure,
    );

    if (this.props.onSave) {
      this.props.onSave();
    }
  };

  private onSetAddressSuccess = (): void => {
    if (this.props.mode !== "onboarding") {
      this.props.snackBarOpen("Your primary work location was set.");
    }
  };

  private onSetAddressFailure = (): void => {
    this.props.snackBarOpen(
      "Sorry, there was a problem saving your primary work location. Please try again.",
    );
  };

  private onEditAddress = () => {
    this.setState({ showAddressSearch: true });
  };

  private onDeleteAddress = () => {
    const { org } = this.props;
    track(TrackingEvents.SETTINGS.WORK_ADDRESS_REMOVE);
    setWorkAddressForUser(
      this.props.relay.environment,
      { orgRelayId: org.id, rawAddress: "" },
      this.onDeleteAddressSuccess,
      this.onDeleteAddressFailure,
    );
    this.setState({ address: null });
  };

  private onDeleteAddressSuccess = (): void => {
    this.props.snackBarOpen("Your primary work location was removed.");
  };

  private onDeleteAddressFailure = (): void => {
    this.props.snackBarOpen(
      "Sorry, there was a problem removing your primary work location. Please try again.",
    );
  };

  public render() {
    const { mode, isWebSettings } = this.props;
    const { showAddressSearch, address } = this.state;

    return (
      <div>
        {isWebSettings ? (
          <Typography variant="h3" className="cw-mb-4">
            When do you typically like to eat lunch?
          </Typography>
        ) : mode === "standard" ? (
          <H2>What is your primary work location?</H2>
        ) : null}
        {isWebSettings ? (
          <Typography variant="subtitle1" className="cw-mb-6">
            We'll use this information for determining which events need travel time and estimating
            travel duration.
          </Typography>
        ) : mode === "standard" ? (
          <div className="cw-text-slate-standard cw-text-14 cw-leading-normal cw-pt-3 cw-pb-4 cw-pr-20">
            We'll use this information for determining which events need travel time and estimating
            travel duration.
          </div>
        ) : null}
        {!showAddressSearch && mode === "standard" ? (
          address ? (
            <div className="cw-flex cw-justify-between cw-items-center cw-w-full cw-gap-1">
              <div className="cw-flex-grow">{address}</div>
              <Button variant="text" startIcon={Edit} onClick={this.onEditAddress} />
              <Button variant="text" startIcon={Delete} onClick={this.onDeleteAddress} />
            </div>
          ) : (
            <Button onClick={this.onClickSetWorkLocation} variant="outlined">
              Set your work location
            </Button>
          )
        ) : (
          <AddressSearch
            label="Work location"
            onSelectAddress={this.onSelectAddress}
            onCancel={this.onCancel}
            mode={mode}
            initialQuery={this.state.query}
          />
        )}
      </div>
    );
  }
}

///////////
// Redux //
///////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const WorkAddressRedux = connect(mapStateToProps, {
  snackBarOpen,
})(WorkAddressCmpt);

///////////
// Relay //
///////////
export const WorkAddress = createFragmentContainer<IContainer>(
  WorkAddressRedux,
  workAddressFragments,
);
